import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { computed, extendObservable } from 'mobx'
import PropTypes from 'prop-types'
import DeleteIcon from '@components/Icon/Delete'
import Action from '@components/Action'
import AddIcon from '@components/Icon/Add'
import { showDialog, isCancellationError } from '@components/Dialog'
import { formatNumber, formatDate } from '@atheneum/api/dist/lib'
import ACL from '@components/ACL'

class InvoiceItems extends Component {
  static propTypes = {
    noActions: PropTypes.bool,
    invoiceStore: PropTypes.object,
    staticData: PropTypes.object,
    invoice: PropTypes.object,
    onAddInvoiceItems: PropTypes.func,
    isEditingDisabled: PropTypes.bool,
    growlStore: PropTypes.object
  }

  constructor(props) {
    super(props)
    extendObservable(this, {
      allowInvoiceItemRemoval: computed(
        () => !props.noActions && !props.isEditingDisabled
      )
    })
  }

  componentDidUpdate() {
    const { invoice } = this.props.invoiceStore
    if (invoice) {
      this.props.invoiceStore.getCapiInvoice(invoice.id)
    }
  }

  removeInvoiceItem = invoiceItem => {
    const { invoiceStore } = this.props
    const { invoice } = invoiceStore

    const title = invoiceStore.isPayg
      ? 'Remove this fee from invoice?'
      : 'Remove this invoice item from invoice?'

    return showDialog({
      title,
      text: invoiceItem.description,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    })
      .then(() => invoiceStore.removeInvoiceItem(invoice.id, invoiceItem.id))
      .catch(error => {
        if (isCancellationError(error)) return
        const { growlStore } = this.props
        growlStore.showError(error)
      })
  }

  renderActions = () => {
    const actions = !this.props.isEditingDisabled && (
      <ul className='actions right-list'>
        <Action alignRight>
          {!(this.props.invoiceStore.isCapiInvoice && this.props.invoiceStore.isCapiCallOnlyInvoice) && <li onClick={this.props.onAddInvoiceItems}>
            <AddIcon />
            <span>Add invoice items</span>
          </li>}
        </Action>
      </ul>
    )

    return (
      <div className='actions-wrapper'>
        <ACL roles={['Accounting', 'Admin']}>{actions}</ACL>
        <ul className='actions left-list'>
          <h2>Invoice items</h2>
        </ul>
      </div>
    )
  }

  renderInvoiceItems = () => {
    const { staticData, invoice } = this.props
    const isPaygInvoice =
      invoice.invoiceTypeId === staticData.CONST.INVOICE_TYPES.PAYG

    const invoiceItems = invoice.invoiceItems.map(invoiceItem => {
      const readableCurrency = staticData.names.currencies[invoice.currencyId]
      const netAmount = invoiceItem.netAmount || 0
      const taxAmount = (netAmount * invoice.vat) / 100 || 0
      const grossAmount = netAmount + taxAmount || 0

      return (
        <tr key={invoiceItem.id}>
          <td style={{ width: '400px' }}>
            <span className='text-wrapper'>{invoiceItem.description}</span>
          </td>
          {isPaygInvoice && (
            <td>
              <span className='text-wrapper'>
                {invoiceItem.deliveryDate
                  ? formatDate(invoiceItem.deliveryDate)
                  : '-'}
              </span>
            </td>
          )}
          <td>
            <span className='text-wrapper'>
              {formatNumber(netAmount)} {readableCurrency}
            </span>
          </td>
          <td>
            <span className='text-wrapper'>
              {formatNumber(taxAmount)} {readableCurrency}
            </span>
          </td>
          <td style={{ textAlign: 'right', width: '110px' }}>
            <span className='text-wrapper'>
              {formatNumber(grossAmount)} {readableCurrency}
            </span>
          </td>
          {this.allowInvoiceItemRemoval && (
            <ACL roles={['Accounting', 'Admin']}>
              <td
                style={{ width: '20px' }}
                onClick={() => this.removeInvoiceItem(invoiceItem)}
              >
                <DeleteIcon />
              </td>
            </ACL>
          )}
        </tr>
      )
    })

    return <tbody>{invoiceItems}</tbody>
  }

  renderNoInvoiceItems = () => {
    return (
      <div>
        {this.renderActions()}
        <h3 className='no-content-message'>No invoice items</h3>
      </div>
    )
  }

  render() {
    const { invoice, staticData } = this.props
    const { invoiceItems } = invoice

    if (!invoiceItems.length) return this.renderNoInvoiceItems()

    const totalGrossAmount = invoiceItems.reduce((totalValue, invoiceItem) => {
      return (
        totalValue +
        invoiceItem.netAmount +
        (invoiceItem.netAmount * invoice.vat) / 100
      )
    }, 0)
    const readableCurrency = staticData.names.currencies[invoice.currencyId]
    const isPaygInvoice =
      invoice.invoiceTypeId === staticData.CONST.INVOICE_TYPES.PAYG

    return (
      <div>
        {this.renderActions()}

        <table className='table-wrapper--nested invoice-table'>
          <thead>
            <tr>
              <th>Description</th>
              {isPaygInvoice && <th>Delivery date</th>}
              {/* <th>Apllic. contract</th> */}
              <th>Amount</th>
              <th>VAT</th>
              <th style={{ textAlign: 'right' }}>Gross amount</th>

              {this.allowInvoiceItemRemoval && <th style={{ width: '20px' }} />}
            </tr>
          </thead>

          {this.renderInvoiceItems()}

          <tfoot>
            <tr className='title'>
              <td colSpan={isPaygInvoice ? 4 : 3}>Total</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {formatNumber(totalGrossAmount)} {readableCurrency}
              </td>

              {this.allowInvoiceItemRemoval && <td style={{ width: '20px' }} />}
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }
}

export default inject(
  'staticData',
  'invoiceStore',
  'growlStore'
)(observer(InvoiceItems))
